import { useEffect, useState } from 'react'
import { api, query } from '../api/client'
import { useQuery } from '../api/hooks'
import type { FiscalPeriod, PeriodCloseStatus } from '../api/types'
import { useAuth } from '../auth/AuthContext'
import { P } from '../auth/permissions'
import {
  ErrorBanner,
  Loading,
  Panel,
  StatusTag,
  formatDate,
  formatDateTime,
  money,
} from '../components/ui'

/**
 * Period close and lock, driven by the month-end checklist in OPERATIONS §4.
 *
 * The blocking items are the checks that make the numbers wrong if skipped, so the close button
 * stays disabled until every one of them is ticked.
 */
export function PeriodsPage() {
  const { can } = useAuth()
  const year = new Date().getFullYear()
  const [selectedId, setSelectedId] = useState<string>('')
  const [error, setError] = useState<unknown>()
  const [busy, setBusy] = useState(false)

  const periods = useQuery<FiscalPeriod[]>(`/periods${query({ year })}`)
  const status = useQuery<PeriodCloseStatus>(
    selectedId ? `/periods/${selectedId}/close-status` : null,
    [selectedId],
  )

  useEffect(() => {
    if (!selectedId && periods.data?.length) {
      const open = periods.data.find((p) => p.state === 'Open' && p.month !== 13)
      setSelectedId((open ?? periods.data[0]).id)
    }
  }, [periods.data, selectedId])

  async function act(path: string, body?: unknown) {
    setBusy(true)
    setError(undefined)

    try {
      await api(path, { method: 'POST', body })
      status.reload()
      periods.reload()
    } catch (caught) {
      setError(caught)
    } finally {
      setBusy(false)
    }
  }

  return (
    <>
      <ErrorBanner error={error ?? periods.error ?? status.error} onDismiss={() => setError(undefined)} />

      <div className="grid cols-2">
        <Panel title={`Periods — ${year}`} flush>
          {periods.loading && <Loading />}

          {periods.data && (
            <table>
              <thead>
                <tr>
                  <th>Period</th>
                  <th>Dates</th>
                  <th style={{ width: 90 }}>State</th>
                  <th style={{ width: 140 }}>Closed by</th>
                </tr>
              </thead>
              <tbody>
                {periods.data.map((period) => (
                  <tr
                    key={period.id}
                    onClick={() => setSelectedId(period.id)}
                    style={{
                      cursor: 'pointer',
                      background: period.id === selectedId ? 'var(--brand-soft)' : undefined,
                    }}
                  >
                    {/* The period label is a fiscal ordinal, not a calendar month: in an office
                        whose year opens in July, period 1 is July and reads "2026-01". The calendar
                        month goes beneath it so the two are never confused. */}
                    <td className="strong">
                      {period.month === 13 ? `${period.year} — adjustment` : `${period.year}-${String(period.month).padStart(2, '0')}`}
                      {period.month !== 13 && (
                        <div className="muted" style={{ fontSize: 11, fontWeight: 400 }}>
                          {new Date(period.startDate).toLocaleDateString(undefined, {
                            month: 'short',
                            year: 'numeric',
                          })}
                        </div>
                      )}
                    </td>
                    <td className="soft">
                      {formatDate(period.startDate)} – {formatDate(period.endDate)}
                    </td>
                    <td>
                      <StatusTag status={period.state} />
                    </td>
                    <td className="soft">{period.closedBy ?? period.lockedBy}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Panel>

        {status.data && (
          <Panel
            title={`Close checklist — ${status.data.label}`}
            actions={<StatusTag status={status.data.state} />}
          >
            <div className="grid cols-3" style={{ marginBottom: 14 }}>
              <div className={status.data.draftEntryCount > 0 ? 'metric alert' : 'metric good'}>
                <div className="label">Draft entries</div>
                <div className="value">{status.data.draftEntryCount}</div>
                <div className="hint">Must be zero to close.</div>
              </div>

              <div className={Math.abs(status.data.clientFundsVariance) > 0.01 ? 'metric alert' : 'metric good'}>
                <div className="label">Client funds variance</div>
                <div className="value">{money(status.data.clientFundsVariance)}</div>
                <div className="hint">Custody + settlements vs cash held.</div>
              </div>

              <div className={status.data.trialBalanceBalances ? 'metric good' : 'metric alert'}>
                <div className="label">Trial balance</div>
                <div className="value">{status.data.trialBalanceBalances ? 'Balanced' : 'Out'}</div>
                <div className="hint">{status.data.unpostedDocumentCount} documents unposted.</div>
              </div>
            </div>

            {/* Accruals waiting to unwind. Its own panel rather than a checklist row, because it is
                the one item on this screen nobody can honestly tick by hand — the unwinding entries
                either exist in the next period or they do not (POSTING_MATRIX §C5). */}
            {status.data.autoReversals.pendingCount > 0 && (
              <div className="banner warn" style={{ marginBottom: 14 }}>
                <div>
                  <strong>
                    {status.data.autoReversals.pendingCount} accrual
                    {status.data.autoReversals.pendingCount === 1 ? '' : 's'} flagged to reverse
                    next period
                  </strong>{' '}
                  — {money(status.data.autoReversals.pendingBaseAmount)}, to be unwound into{' '}
                  {status.data.autoReversals.nextPeriodLabel ?? 'the next period'}. The period
                  cannot close until they are raised.
                  {!status.data.autoReversals.nextPeriodLabel && (
                    <div style={{ marginTop: 4 }}>
                      There is no period after this one. Open next year before closing.
                    </div>
                  )}
                  {status.data.autoReversals.nextPeriodLabel &&
                    !status.data.autoReversals.nextPeriodAcceptsPostings && (
                      <div style={{ marginTop: 4 }}>
                        {status.data.autoReversals.nextPeriodLabel} will not accept postings.
                        Reopen it first.
                      </div>
                    )}
                </div>

                {status.data.state === 'Open' && can(P.periods.close) && (
                  <button
                    className="primary"
                    disabled={
                      busy ||
                      !status.data.autoReversals.nextPeriodLabel ||
                      !status.data.autoReversals.nextPeriodAcceptsPostings
                    }
                    onClick={() => void act(`/periods/${selectedId}/auto-reversals`)}
                  >
                    Raise auto-reversals
                  </button>
                )}
              </div>
            )}

            {status.data.checklist.map((item) => (
              <div className="checklist-item" key={item.key}>
                <input
                  type="checkbox"
                  checked={item.isDone}
                  disabled={status.data!.state !== 'Open' || !can(P.periods.close) || busy}
                  onChange={(e) =>
                    void act(
                      `/periods/${selectedId}/checklist/${item.key}${query({ done: e.target.checked })}`,
                    )
                  }
                />
                <div className="label">
                  {item.label}
                  {item.isBlocking && <span className="tag warn" style={{ marginLeft: 6 }}>required</span>}
                  {item.checkedBy && (
                    <div className="muted" style={{ fontSize: 11 }}>
                      {item.checkedBy} · {formatDateTime(item.checkedAt)}
                    </div>
                  )}
                </div>
              </div>
            ))}

            <div className="toolbar" style={{ marginTop: 16 }}>
              <div className="spacer" />

              {status.data.state === 'Open' && can(P.periods.close) && (
                <button
                  className="primary"
                  disabled={!status.data.canClose || busy}
                  onClick={() => void act(`/periods/${selectedId}/close`)}
                >
                  Close period
                </button>
              )}

              {status.data.state === 'Closed' && can(P.periods.lock) && (
                <button
                  className="primary"
                  disabled={busy}
                  onClick={() => void act(`/periods/${selectedId}/lock`)}
                >
                  Lock permanently
                </button>
              )}

              {status.data.state !== 'Open' && can(P.periods.reopen) && (
                <button
                  className="danger"
                  disabled={busy}
                  onClick={() => {
                    const reason = window.prompt('Why is this period being reopened? This is recorded permanently.')
                    if (reason?.trim()) void act(`/periods/${selectedId}/reopen`, { reason })
                  }}
                >
                  Reopen
                </button>
              )}
            </div>

            {status.data.state === 'Open' && !status.data.canClose && (
              <div className="muted" style={{ fontSize: 12, marginTop: 8 }}>
                Every required check must be ticked, all drafts posted or voided, and every flagged
                accrual unwound before the period can be closed.
              </div>
            )}
          </Panel>
        )}
      </div>
    </>
  )
}
